import React from 'react'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import { useSmartTranslation } from '../../../../generic/hooks/useSmartTranslation'
import { useDateFormatter } from '../../../../generic/hooks/useDateFormatter'
import { formatDuration } from '../duration'
import { theoreticalTabs } from './WorkflowTheoreticalTabs'
import { DetailTabs, ExpansionDatum, ExpansionSection } from './expansionParts'
import { isBlank } from '../detailShared'

// ⚠️⚠️ S426 / S430 (plan «Workflow teórico · Progress real», aprobado por el PO el 23/09) — ESTE
// DESPLEGABLE SOLO ENSEÑA LO PLANIFICADO. Todo lo real que tenía —el titular de cifras, el aviso de
// atención, la tarjeta de tiempo, el histórico de estados, reportes y autocontroles— se mudó al
// desplegable de Progress (`ProgressOperationDetails`), y S440 fija que nada se perdió por el camino.
// Lo que queda son PESTAÑAS con su recuento: Tareas · Recursos · Calidad (S427-S429, de su propia
// puerta, `…/theoretical`) y Preparación (S430, de N4). ⚠️ Si la puerta teórica FALLA, sus tres
// pestañas NO se pintan vacías —un «0» afirmaría que la operación no tiene tareas— y se dice que no
// se pudo cargar. Mientras carga, su recuento es «…».
const OperationExpansionDetails = ({ data, row = {}, theoretical = { status: 'loading', data: {} } }) => {
  const { t_ } = useSmartTranslation()
  const { formatDateTime } = useDateFormatter()
  // ADDENDUM 30 A: esta constante es la AUSENCIA del dato, no el estado "pendiente".
  const notReported = t_('Not reported')
  const duracion = (value) => (isBlank(value) ? notReported : formatDuration(value))

  // S430 — el RECUENTO de Preparación son los datos que traen valor. ⚠️ En la base del PO son todos
  // ausentes menos `external` —ninguna operación gestiona preparación (S323, addendum 61)—, y la
  // pestaña lo dice con su número en vez de esconderse: la ausencia se declara dato a dato.
  const preparationValues = [
    data.initialPreparationSeconds, data.initialPreparationOperators,
    data.intermediatePreparationSeconds, data.intermediatePreparationOperators,
    data.requiredOperators, data.external, data.remark, data.lastUpdatedAt
  ]
  const preparationCount = preparationValues.filter((value) => !isBlank(value)).length

  const preparation = (
    <Stack spacing={2}>
      {/* ⚠️⚠️ S323 — desde el addendum 61 Sun lee `manage_preparation` y sirve los CUATRO ausentes
          cuando la operación no gestiona preparación (7.421 de 7.421 jobs medidos). `duracion()` e
          `isBlank` dicen «Sin informar» ante la ausencia y enseñan el cero cuando es un cero. */}
      <ExpansionSection title={t_('Preparation')}>
        <ExpansionDatum label={t_('Initial')} testId='expansion-initial-preparation'>
          {duracion(data.initialPreparationSeconds)}
        </ExpansionDatum>
        <ExpansionDatum label={t_('Initial operators')} testId='expansion-initial-preparation-operators'>
          {isBlank(data.initialPreparationOperators) ? notReported : data.initialPreparationOperators}
        </ExpansionDatum>
        <ExpansionDatum label={t_('Intermediate')} testId='expansion-intermediate-preparation'>
          {duracion(data.intermediatePreparationSeconds)}
        </ExpansionDatum>
        <ExpansionDatum label={t_('Intermediate operators')} testId='expansion-intermediate-preparation-operators'>
          {isBlank(data.intermediatePreparationOperators) ? notReported : data.intermediatePreparationOperators}
        </ExpansionDatum>
      </ExpansionSection>
      <ExpansionSection columns={4}>
        {/* ⚠️ Regla CONTRARIA a la de la preparación, y la diferencia es deliberada: aquí un cero no
            se distingue de un campo sin rellenar (131 de 2.907 jobs lo traen), así que la ausencia
            no se afirma como "requiere cero operarios". */}
        <ExpansionDatum label={t_('Operators required')} testId='expansion-required-operators'>
          {isBlank(data.requiredOperators) ? notReported : data.requiredOperators}
        </ExpansionDatum>
        {/* S340 (ADDENDUM 18 / E24): la marca de job EXTERNO, con la MISMA palabra y la misma forma
            de casilla que el editor de workflow (la casilla EXT). Se pinta también cuando es
            `false`: no es una ausencia que callar, es un hecho del job. Casilla MÁS palabra: el
            estado nunca se distingue solo por un icono ni solo por color. */}
        <ExpansionDatum label={t_('Extern')} testId='expansion-external'>
          {/* S341: TRES estados, y el tercero se mira PRIMERO. La ausencia no es ninguna casilla
              —una casilla ya es una respuesta—, y apunta al servidor, no al taller. El orden del
              ternario NO es estilo: `external` vale `''` cuando falta, que es falsy. */}
          <Stack direction='row' spacing={0.5} alignItems='center'>
            {data.external === ''
              ? <HelpOutlineIcon data-testid='expansion-external-unknown' fontSize='small' sx={{ color: 'text.secondary' }} />
              : data.external
                ? <CheckBoxIcon data-testid='expansion-external-on' fontSize='small' sx={{ color: 'primary.main' }} />
                : <CheckBoxOutlineBlankIcon data-testid='expansion-external-off' fontSize='small' sx={{ color: 'text.secondary' }} />}
            <Typography
              component='span'
              variant='body2'
              sx={{ fontWeight: 700, color: data.external === '' ? 'text.secondary' : 'text.primary' }}
            >
              {data.external === '' ? t_('Not reported by the server') : data.external ? t_('Yes') : t_('No')}
            </Typography>
          </Stack>
        </ExpansionDatum>
        <ExpansionDatum label={t_('Remark')} testId='expansion-remark'>
          {isBlank(data.remark) ? notReported : data.remark}
        </ExpansionDatum>
        <ExpansionDatum label={t_('Last updated')} testId='expansion-last-updated'>
          {isBlank(data.lastUpdatedAt) ? notReported : formatDateTime(data.lastUpdatedAt)}
        </ExpansionDatum>
      </ExpansionSection>
    </Stack>
  )

  return (
    <Stack spacing={1}>
      {theoretical.status === 'error' && (
        <Typography data-testid='workflow-theoretical-error' variant='body2' sx={{ color: 'error.main' }}>
          {t_('Could not load the planned tasks, resources and quality')}
        </Typography>
      )}
      <DetailTabs
        testId='workflow-operation-tabs'
        tabs={[...theoreticalTabs(theoretical, t_, row), { key: 'preparation', label: t_('Preparation'), count: preparationCount, content: preparation }]}
      />
    </Stack>
  )
}

export default OperationExpansionDetails
